/**
 * This component is a group of select Field
 *
 * @module views/components/DependentSelectField
 * @memberof -Common
 */
import './DependentSelectField.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import Icon from '@ulta/core/components/Icon/Icon';
import Markdown from '@ulta/core/components/Markdown/Markdown';
import SelectField from '@ulta/core/components/SelectField_New/SelectField_New';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import DependentField from '@ulta/modules/DependentField/DependentField';

import * as utils from './DependentSelectField';

/**
 * Represents a DependentSelectField component
 *
 * @method
 * @param { DependentSelectFieldProps} props - React properties passed from composition
 * @returns DependentSelectField
 */
export const DependentSelectField = function( props ){
  const { field, dependentFields, level, name, enabled, required } = props;

  const selectRef = useRef();
  const [activeDependentField, setActiveDependentField] = useState();
  const [currentvalue, setCurrentValue] = useState();
  const [selectedValue, setSelectedValue] = useState();
  const [forceRender, setForceRender] = useState( false );

  const dependentField = dependentFields?.find( fieldItem => fieldItem.key === field.options[0].value );
  const defaultDependentField = dependentField?.dependentChildField;
  const toolTipData = field?.options?.find( tip => tip.value === selectedValue )?.toolTipValue;
  const hasDependentFieldData = activeDependentField || defaultDependentField;

  const handleOnChange = useCallback(
    utils.composeHandleOnChange(
      { dependentFields, selectRef },
      { setActiveDependentField, setSelectedValue, setForceRender }
    ),
    [dependentFields, setActiveDependentField, setSelectedValue, setForceRender]
  );

  useEffect( () => {
    setCurrentValue( toolTipData );
  }, [toolTipData] );

  return (
    <>
      <div className='DependentSelectField__Select'
        ref={ selectRef }
      >
        <SelectField
          key={ forceRender }
          onChange={ handleOnChange }
          name={ `${ name }_${ level }` }
          { ...spreadProps( { ...field } ) }
          enabled={ enabled }
        />
      </div>
      { hasDependentFieldData &&
        <DependentField
          key={ forceRender }
          dependentFields={ activeDependentField ? activeDependentField.dependentFields : defaultDependentField.dependentFields }
          field={ activeDependentField ? { ...activeDependentField?.field[0], enabled: true } : { ...defaultDependentField?.field[0], required: true } }
          level={ level + 1 }
          name={ name }
        />
      }
      { currentvalue &&
        <div className='DependentSelectField__toolTip'>
          <div className='DependentSelectField__toolTipIcon'>
            <Icon
              aria-hidden={ true }
              size='m'
              name={ 'infocircle' }
              className={ 'DependentSelectField__toolTipIcon--infocircle' }
            />
          </div>
          <div className='DependentSelectField__toolTipMessage'>
            <Markdown
              bodyStyle='body-2'
              cmsMarkdownContent={ currentvalue }
              className='DependentSelectField__toolTipMessage'
            />
          </div>
        </div>
      }
    </>
  );
};

/**
 *
 * @param {object} data - Arguments
 * @param {object} methods - Methods
 */
export const composeHandleOnChange = ( data, methods ) => ( value ) => {
  const { dependentFields, selectRef } = data || {};
  const { setActiveDependentField, setSelectedValue, setForceRender } = methods || {};
  const dependentField = Array.isArray( dependentFields ) && dependentFields.find( ( fieldItem ) => fieldItem.key === value );
  const selectEl = selectRef.current.nextSibling?.querySelector( 'select' );
  if( selectEl ){
    selectEl.value = value;
    const event = new Event( 'change', { bubbles: true } );
    selectEl.dispatchEvent( event );
  }
  setActiveDependentField( dependentField?.dependentChildField );
  setSelectedValue( value );
  setForceRender( prevState => !prevState );
};

/**
 * Property type definitions
 * @typedef DependentSelectFieldProps
 * @type {object}
 * @property {object} field - sets field
 * @property {array} dependentFields - sets dependentFields
 * @property {number} level - sets level
 * @property {string} name - sets name
 * @property {boolean} enabled - boolean to enable/disable
 * @property {boolean} required - boolean
 */
export const propTypes =  {
  /** sets field */
  field: PropTypes.object,
  /** Sets dependentFields */
  dependentFields: PropTypes.array,
  /** Sets the level */
  level: PropTypes.number,
  /** Sets the name */
  name: PropTypes.string,
  /** boolean for enable/disable */
  enabled: PropTypes.bool,
  required: PropTypes.bool
};

DependentSelectField.propTypes = propTypes;

export default DependentSelectField;
